import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { ApiError, normalizeApiError } from "./errorUtils";
import { isEndpointInList } from "./pathUtils";
import { appConfig } from "../appConfig";
import { Mutex } from "async-mutex";

const axiosInstance: AxiosInstance = axios.create({
  withCredentials: true, // Browser automatically sends cookies
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
});

/**
 * Global mutex
 * Prevents multiple simultaneous refresh-token requests
 */
const mutex = new Mutex();

/**
 * Response interceptor
 * - Handles 401
 * - Performs single refresh request
 * - Queues concurrent requests behind mutex
 */
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (err: AxiosError) => {
    // Network / timeout errors
    if (!err.response) throw normalizeApiError(err, "NETWORK_ERROR");

    const originalConfig = err.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    const status = err.response.status;
    const requestUrl = originalConfig?.url ?? "";
    const isAuthEndpoint = isEndpointInList(requestUrl, appConfig.authEndpoints);

    /**
     * Only handle:
     * - 401
     * - non-auth endpoints
     */
    if (status === 401 && !isAuthEndpoint) {
      /**
       * Avoid infinite retry loops
       */
      if (originalConfig?._retry) {
        const nf: ApiError = {
          ...normalizeApiError(err, "REFRESH_FAILED"),
          status: 403,
          errorCode: "REFRESH_FAILED",
          globalErrors: ["errors.REFRESH_FAILED"],
        };

        throw nf;
      }

      /**
       * Wait if another refresh is already running
       */
      await mutex.waitForUnlock();

      /**
       * If no refresh is running → perform refresh
       */
      if (!mutex.isLocked()) {
        const release = await mutex.acquire();

        try {
          /**
           * Mark original request
           */
          if (originalConfig) {
            originalConfig._retry = true;
          }

          /**
           * Refresh tokens
           */
          await axios.post(appConfig.refreshUrl, undefined, {
            withCredentials: true,
          });

          /**
           * Retry original request
           */
          if (originalConfig) {
            return axiosInstance(originalConfig);
          }

          const nf: ApiError = {
            ...normalizeApiError(err, "REFRESH_FAILED"),
            status: 403,
            errorCode: "REFRESH_FAILED",
            globalErrors: ["errors.REFRESH_FAILED"],
          };

          throw nf;
        } catch (refreshErr) {
          const nf: ApiError = {
            ...normalizeApiError(refreshErr, "REFRESH_FAILED"),
            status: 403,
            errorCode: "REFRESH_FAILED",
            globalErrors: ["errors.REFRESH_FAILED"],
          };

          throw nf;
        } finally {
          release();
        }
      } else {
        /**
         * Another request already refreshed successfully
         * Retry original request after mutex unlock
         */

        if (originalConfig) {
          originalConfig._retry = true;

          return axiosInstance(originalConfig);
        }
      }
    }

    /**
     * Other errors
     */
    throw normalizeApiError(err);
  },
);

/**
 * Unified request wrapper
 */
export async function httpRequest<T = any>(cfg: AxiosRequestConfig): Promise<T> {
  try {
    const res = await axiosInstance.request<T>(cfg);
    return res.data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const apiError = normalizeApiError(axiosError) as ApiError & { config?: AxiosRequestConfig };
    apiError.config = cfg;
    throw apiError;
  }
}

/**
 * Helper methods
 */
export const axiosHelper = {
  get: <T = any>(url: string, cfg: Omit<AxiosRequestConfig, "url" | "method"> = {}) => httpRequest<T>({ ...cfg, url, method: "GET" }),

  post: <T = any>(url: string, data?: any, cfg: Omit<AxiosRequestConfig, "url" | "method" | "data"> = {}) =>
    httpRequest<T>({ ...cfg, url, data, method: "POST" }),

  put: <T = any>(url: string, data?: any, cfg: Omit<AxiosRequestConfig, "url" | "method" | "data"> = {}) =>
    httpRequest<T>({ ...cfg, url, data, method: "PUT" }),

  patch: <T = any>(url: string, data?: any, cfg: Omit<AxiosRequestConfig, "url" | "method" | "data"> = {}) =>
    httpRequest<T>({ ...cfg, url, data, method: "PATCH" }),

  delete: <T = any>(url: string, cfg: Omit<AxiosRequestConfig, "url" | "method"> = {}) => httpRequest<T>({ ...cfg, url, method: "DELETE" }),
};
